import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiJson } from '../api/client';

const API_BASE = import.meta.env.VITE_API_BASE as string;

interface YesterdayTask {
  id: string;
  name: string;
  prompt: string;
  schedule_type: 'daily' | 'weekly' | 'monthly';
  schedule_time: string;
  day_of_week: number | null;
  day_of_month: number | null;
  max_occurrences: number;
  occurrences_used: number;
  self_renew: number;
  status: 'active' | 'paused' | 'completed' | 'expired';
  next_run: number;
  last_run: number | null;
  last_result: string | null;
  last_error: string | null;
  koji_codename: string;
  created_at: number;
  // Stage 29: surfaced via LEFT JOIN against bulletin_panels so the
  // task row can show a "Bound to bulletin: <koji>" badge inline.
  bound_panel_id: string | null;
  bound_panel_koji: string | null;
  bound_panel_label: string | null;
  // Stage 30: 0 = Stage 5 zero-tool execution; 1 = orchestrator with
  // scheduled-safe tool registry, skill bridge, 90s wall-time race,
  // and (when bound to a panel) write_bulletin_html.
  enable_tools: number;
}

interface ListResponse {
  tasks: YesterdayTask[];
}

interface BulletinRun {
  runAt: number;
  r2Key: string;
  signedUrl: string;
  preview: string;
}
interface BulletinTask {
  taskId: string;
  name: string;
  koji_codename: string;
  runs: BulletinRun[];
}
interface BulletinResponse {
  bulletin: BulletinTask[];
  cached: boolean;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatUnix(s: number | null): string {
  if (!s) return '—';
  try {
    return new Date(s * 1000).toLocaleString();
  } catch {
    return String(s);
  }
}

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

interface FormState {
  name: string;
  prompt: string;
  schedule_type: 'daily' | 'weekly' | 'monthly';
  schedule_time: string;
  day_of_week: number;
  day_of_month: number;
  max_occurrences: number;
  self_renew: boolean;
  // Stage 30: opt-in tool execution.
  enable_tools: boolean;
}

// Stage 36: canonical default prompt template. Pre-fills the
// New-task form. Users replace [TASK] manually before submitting.
// The chain (web_search → grounding.md → optional image →
// basel-frontend-design + TINS skills → write_bulletin_html)
// requires enable_tools so the New-task form pre-checks the
// corresponding checkbox. Step 2 is optional — delete it for
// text-only panels.
const DEFAULT_PROMPT_TEMPLATE =
  '1. we can use the web search tool to find information on [TASK] ' +
  'and write a markdown file named "grounding.md",\n' +
  '2. (optional, for visual-rich tasks) call ' +
  'prepare_image_for_panel with a cartoon-style prompt based on the ' +
  'topic to get a worker-served image URL — embed it later in the ' +
  'html via <img src="..." alt="...">. Skip this step if the panel ' +
  'is text-only.\n' +
  '3. once written "grounding.md" we will use with the ' +
  'basel-frontend-design skill to write detailed design instructions ' +
  'for the creation of the html page visualising the information in ' +
  'the grounding markdown artifact, we can write these detailed ' +
  'instructions inside a new markdown artifact named ' +
  '"website-creation-TINS.md" using the TINS skill including all ' +
  'steps and code required to complete the implementation.\n' +
  '4. we will use the "website-creation-TINS.md" to create the ' +
  'planned HTML page (embedding the image URL via <img src="..."> ' +
  'if step 2 was used).';

function emptyForm(): FormState {
  return {
    name: '',
    prompt: DEFAULT_PROMPT_TEMPLATE,
    schedule_type: 'daily',
    schedule_time: '09:00',
    day_of_week: 1,
    day_of_month: 1,
    max_occurrences: 30,
    self_renew: false,
    // The default template needs tools to do anything useful.
    enable_tools: true,
  };
}

export function YesterdayTab() {
  const [tasks, setTasks] = useState<YesterdayTask[] | null>(null);
  const [bulletin, setBulletin] = useState<BulletinTask[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<YesterdayTask | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const refresh = async (): Promise<void> => {
    setErr(null);
    try {
      const res = await apiGet<ListResponse>('/api/yesterday');
      setTasks(res.tasks);
      const b = await apiGet<BulletinResponse>('/api/yesterday/bulletin');
      setBulletin(b.bulletin);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const openNew = (): void => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (t: YesterdayTask): void => {
    setEditing(t);
    setForm({
      name: t.name,
      prompt: t.prompt,
      schedule_type: t.schedule_type,
      schedule_time: t.schedule_time,
      day_of_week: t.day_of_week ?? 1,
      day_of_month: t.day_of_month ?? 1,
      max_occurrences: t.max_occurrences,
      self_renew: !!t.self_renew,
      enable_tools: !!t.enable_tools,
    });
    setShowForm(true);
  };

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        prompt: form.prompt,
        schedule_type: form.schedule_type,
        schedule_time: form.schedule_time,
        max_occurrences: form.max_occurrences,
        self_renew: form.self_renew,
        enable_tools: form.enable_tools,
      };
      if (form.schedule_type === 'weekly') payload.day_of_week = form.day_of_week;
      if (form.schedule_type === 'monthly') payload.day_of_month = form.day_of_month;
      if (editing) {
        await apiJson('PUT', `/api/yesterday/${encodeURIComponent(editing.id)}`, payload);
      } else {
        await apiJson('POST', '/api/yesterday', payload);
      }
      setShowForm(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const runNow = async (t: YesterdayTask): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await apiJson('POST', `/api/yesterday/${encodeURIComponent(t.id)}/run-now`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const togglePause = async (t: YesterdayTask): Promise<void> => {
    const nextStatus = t.status === 'active' ? 'paused' : 'active';
    setBusy(true);
    try {
      await apiJson('PUT', `/api/yesterday/${encodeURIComponent(t.id)}`, {
        status: nextStatus,
      });
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t: YesterdayTask): Promise<void> => {
    if (!confirm(`Delete task "${t.name}"?`)) return;
    setBusy(true);
    try {
      await apiJson('DELETE', `/api/yesterday/${encodeURIComponent(t.id)}`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const bulletinFlat = useMemo(() => {
    if (!bulletin) return [] as Array<{ task: BulletinTask; run: BulletinRun }>;
    const rows: Array<{ task: BulletinTask; run: BulletinRun }> = [];
    for (const t of bulletin) for (const r of t.runs) rows.push({ task: t, run: r });
    rows.sort((a, b) => b.run.runAt - a.run.runAt);
    return rows;
  }, [bulletin]);

  return (
    <div className="stack">
      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3>Yesterday — scheduled prompts</h3>
          <button className="btn-primary" onClick={openNew} disabled={busy}>
            New task
          </button>
        </div>
        <p className="muted small">
          Times are UTC (HH:MM, 24-hour). A cron trigger runs every 60s and
          executes due tasks against the active persona. Output is saved to
          R2 and summarised in the Bulletin below.
        </p>
        {err ? <div className="status-missing">Error: {err}</div> : null}
        {tasks === null ? (
          <div className="muted">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="muted">No scheduled tasks yet.</div>
        ) : (
          <ul className="persona-list">
            {tasks.map((t) => (
              <li key={t.id} className="persona-list-item">
                <div>
                  <div className="persona-list-name">
                    {t.name}{' '}
                    <span className="badge">{t.status}</span>{' '}
                    {t.self_renew ? <span className="badge">renews</span> : null}
                    {t.bound_panel_koji ? (
                      <>
                        {' '}
                        <span
                          className="badge badge-bulletin-bound"
                          title={`Bound to bulletin panel "${t.bound_panel_label ?? t.bound_panel_koji}"`}
                        >
                          📌 Bound to bulletin: {t.bound_panel_koji}
                        </span>
                      </>
                    ) : null}
                  </div>
                  <div className="muted small">
                    {t.schedule_type}
                    {t.schedule_type === 'weekly'
                      ? ` · ${DAYS[t.day_of_week ?? 0]}`
                      : t.schedule_type === 'monthly'
                        ? ` · day ${t.day_of_month ?? 1}`
                        : ''}{' '}
                    at {t.schedule_time} UTC · next {formatUnix(t.next_run)}
                    {' · '}
                    {t.occurrences_used}/{t.max_occurrences} · {t.koji_codename}
                  </div>
                  {t.last_error ? (
                    <div className="status-missing small">
                      Last error: {t.last_error}
                    </div>
                  ) : null}
                </div>
                <div className="row">
                  <button onClick={() => openEdit(t)} disabled={busy}>
                    Edit
                  </button>
                  <button onClick={() => void runNow(t)} disabled={busy}>
                    Run now
                  </button>
                  <button onClick={() => void togglePause(t)} disabled={busy}>
                    {t.status === 'active' ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    className="btn-danger small"
                    onClick={() => void remove(t)}
                    disabled={busy}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showForm ? (
        <div className="panel">
          <h3>{editing ? `Edit: ${editing.name}` : 'New task'}</h3>
          <div className="stack">
            <label>
              <div className="muted small">Name</div>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label>
              <div className="muted small">Prompt</div>
              <textarea
                rows={4}
                value={form.prompt}
                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              />
            </label>
            <div className="row">
              <label>
                <div className="muted small">Schedule</div>
                <select
                  value={form.schedule_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      schedule_type: e.target.value as FormState['schedule_type'],
                    })
                  }
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>
              <label>
                <div className="muted small">Time (UTC, HH:MM)</div>
                <input
                  type="text"
                  value={form.schedule_time}
                  placeholder="09:00"
                  onChange={(e) =>
                    setForm({ ...form, schedule_time: e.target.value })
                  }
                />
              </label>
              {form.schedule_type === 'weekly' ? (
                <label>
                  <div className="muted small">Day of week</div>
                  <select
                    value={form.day_of_week}
                    onChange={(e) =>
                      setForm({ ...form, day_of_week: Number(e.target.value) })
                    }
                  >
                    {DAYS.map((d, i) => (
                      <option key={d} value={i}>
                        {d}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {form.schedule_type === 'monthly' ? (
                <label>
                  <div className="muted small">Day of month (1-31)</div>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={form.day_of_month}
                    onChange={(e) =>
                      setForm({ ...form, day_of_month: Number(e.target.value) })
                    }
                  />
                </label>
              ) : null}
            </div>
            <div className="row">
              <label>
                <div className="muted small">Max occurrences</div>
                <input
                  type="number"
                  min={1}
                  value={form.max_occurrences}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      max_occurrences: Number(e.target.value),
                    })
                  }
                />
              </label>
              <label className="voice-toggle-row">
                <input
                  type="checkbox"
                  checked={form.self_renew}
                  onChange={(e) =>
                    setForm({ ...form, self_renew: e.target.checked })
                  }
                />
                <span>Self-renew after max occurrences</span>
              </label>
            </div>
            {/* Stage 30: opt-in tool execution. */}
            <div className="row">
              <label className="voice-toggle-row">
                <input
                  type="checkbox"
                  checked={form.enable_tools}
                  onChange={(e) =>
                    setForm({ ...form, enable_tools: e.target.checked })
                  }
                />
                <span>Enable tools (web search, image gen, write_bulletin_html)</span>
              </label>
            </div>
            {form.enable_tools ? (
              <p className="muted small">
                When enabled, this task can use web search, generate images /
                music / speech / video, write markdown files, and (when bound
                to a bulletin panel) write HTML directly into the panel. Runs
                are capped at 90 seconds wall time. Costs more tokens than the
                default no-tool path.
              </p>
            ) : null}
            <div className="row">
              <button
                className="btn-primary"
                onClick={() => void submit()}
                disabled={busy || !form.name.trim() || !form.prompt.trim()}
              >
                {editing ? 'Save changes' : 'Create task'}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="panel">
        <h3>Bulletin — last 7 days</h3>
        {bulletin === null ? (
          <div className="muted">Loading…</div>
        ) : bulletinFlat.length === 0 ? (
          <div className="muted">No runs in the past 7 days.</div>
        ) : (
          <ul className="persona-list">
            {bulletinFlat.map(({ task, run }) => (
              <li key={run.r2Key} className="persona-list-item">
                <div>
                  <div className="persona-list-name">{task.name}</div>
                  <div className="muted small">
                    {formatUnix(run.runAt)} · {task.koji_codename}
                  </div>
                  {run.preview ? (
                    <div className="small" style={{ marginTop: 4 }}>
                      {run.preview}
                      {run.preview.length >= 200 ? '…' : ''}
                    </div>
                  ) : null}
                </div>
                <div className="row">
                  <a
                    className="btn-test-voice"
                    href={absolute(run.signedUrl)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
